import HomeScreenTip from "@/components/HomeScreenTip";
import { loadShareOrigin } from "@/lib/origin";
import "@/App.css";
import { useState, useEffect } from "react";
import { AnimatePresence } from "framer-motion";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import BottomNav from "@/components/BottomNav";
import BackToCoach from "@/components/BackToCoach";
import FloatingResetButton from "@/components/FloatingResetButton";
import BrandSplash from "@/components/BrandSplash";
import Resets from "@/pages/Resets";
import { Toaster } from "@/components/ui/sonner";

import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Onboarding from "@/pages/Onboarding";
import Home from "@/pages/Home";
import Talk from "@/pages/Talk";
import Coach from "@/pages/Coach";
import Practice from "@/pages/Practice";
import Roleplay from "@/pages/Roleplay";
import Dating from "@/pages/Dating";
import Inner from "@/pages/Inner";
import Installation from "@/pages/Installation";
import ReturnSetup from "@/pages/ReturnSetup";
import DailyRoutine from "@/pages/DailyRoutine";
import Immersions from "@/pages/Immersions";
import ImmersionEnvironments from "@/pages/ImmersionEnvironments";
import ImmersionPlayer from "@/pages/ImmersionPlayer";
import Missions from "@/pages/Missions";
import Training from "@/pages/Training";
import NightAudio from "@/pages/NightAudio";
import Gift from "@/pages/Gift";
import Invite from "@/pages/Invite";
import Moment from "@/pages/Moment";
import PartnerLink from "@/pages/PartnerLink";
import { WelcomeGate, InstallWelcomeGate } from "@/components/AdelaidaWelcome";
import Progress from "@/pages/Progress";
import Memory from "@/pages/Memory";
import Admin from "@/pages/Admin";
import Upgrade from "@/pages/Upgrade";
import TopUp from "@/pages/TopUp";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Account from "@/pages/Account";
import Settings from "@/pages/Settings";
import Legal from "@/pages/Legal";
import Promo from "@/pages/Promo";
import Partner from "@/pages/Partner";
import ScriptReview from "@/pages/ScriptReview";
import "@/lib/sharedAudio";

function AdminOnly({ children }) {
  const { user } = useAuth();
  return user?.is_admin ? children : <Navigate to="/app" replace />;
}

function SplashGate({ children }) {
  const { ready } = useAuth();
  const [minDone, setMinDone] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMinDone(true), 1100);
    return () => clearTimeout(t);
  }, []);
  const publicShare = /^\/(invite|gift|moment)\//.test(window.location.pathname);
  const show = !publicShare && (!ready || !minDone);
  return (
    <>
      <AnimatePresence>{show && <BrandSplash key="splash" />}</AnimatePresence>
      {children}
    </>
  );
}

loadShareOrigin();

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <ErrorBoundary>
            <HomeScreenTip />
          <SplashGate>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Auth mode="login" />} />
            <Route path="/register" element={<Auth mode="register" />} />
            <Route path="/gift/:rid" element={<Gift />} />
            <Route path="/invite/:token" element={<Invite />} />
            <Route path="/moment/:mid" element={<Moment />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
            <Route path="/app" element={<ProtectedRoute><Home /></ProtectedRoute>} />
            <Route path="/talk" element={<ProtectedRoute><Talk /></ProtectedRoute>} />
            <Route path="/coach" element={<ProtectedRoute><Coach /></ProtectedRoute>} />
            <Route path="/practice" element={<ProtectedRoute><Practice /></ProtectedRoute>} />
            <Route path="/practice/:scenarioId" element={<ProtectedRoute><Roleplay /></ProtectedRoute>} />
            <Route path="/dating" element={<ProtectedRoute><Dating /></ProtectedRoute>} />
            <Route path="/return" element={<ProtectedRoute><ReturnSetup /></ProtectedRoute>} />
            <Route path="/routine" element={<ProtectedRoute><DailyRoutine /></ProtectedRoute>} />
            <Route path="/resets" element={<ProtectedRoute><Resets /></ProtectedRoute>} />
            <Route path="/immersions" element={<ProtectedRoute><Immersions /></ProtectedRoute>} />
            <Route path="/immersions/:topicId" element={<ProtectedRoute><ImmersionEnvironments /></ProtectedRoute>} />
            <Route path="/immersions/:topicId/:envId" element={<ProtectedRoute><ImmersionPlayer /></ProtectedRoute>} />
            <Route path="/inner" element={<ProtectedRoute><Inner /></ProtectedRoute>} />
            <Route path="/inner/:id" element={<ProtectedRoute><Installation /></ProtectedRoute>} />
            <Route path="/missions" element={<ProtectedRoute><Missions /></ProtectedRoute>} />
            <Route path="/training" element={<Navigate to="/inner" replace />} />{/* The Diet: parked until relaunch; restore <ProtectedRoute><AdminOnly><Training /> */}
            <Route path="/audio" element={<ProtectedRoute><NightAudio /></ProtectedRoute>} />
            <Route path="/progress" element={<ProtectedRoute><Progress /></ProtectedRoute>} />
            <Route path="/memory" element={<ProtectedRoute><Memory /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
            <Route path="/admin/scripts" element={<ProtectedRoute><ScriptReview /></ProtectedRoute>} />
            <Route path="/upgrade" element={<ProtectedRoute><Upgrade /></ProtectedRoute>} />
            <Route path="/topup" element={<ProtectedRoute><TopUp /></ProtectedRoute>} />
            <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
            <Route path="/payment/cancel" element={<ProtectedRoute><PaymentCancel /></ProtectedRoute>} />
            <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/legal" element={<ProtectedRoute><Legal /></ProtectedRoute>} />
            <Route path="/promo" element={<Promo />} />
            <Route path="/partner" element={<ProtectedRoute><Partner /></ProtectedRoute>} />
            <Route path="/:slug" element={<PartnerLink />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <WelcomeGate />
          <InstallWelcomeGate />
          <div className="h-16 md:hidden" />
          <FloatingResetButton />
          <BackToCoach />
          <BottomNav />
          <Toaster position="top-center" richColors />
          </SplashGate>
          </ErrorBoundary>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
